/*
 * Los formularios de captura, montados una sola vez y abiertos desde cualquier
 * lado con `abrir("interaccion", {cuentaId})`. Así el botón de captura, la paleta
 * y cada pantalla abren exactamente el mismo formulario.
 *
 * Todos son cortos a propósito: capturar tiene que ser más rápido que WhatsApp.
 */

import { createContext, useCallback, useContext, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { ErrorApi, api, idNuevo, sesion } from "../api/cliente";
import { useAgregarEntrada, useAgregarPersona, useAgregarSesion, useCrearCuenta, useCuentas, useIniciarDescubrimiento, useProponerHallazgo, useRegistrarInteraccion, useZonas } from "../api/hooks";
import { EQUIPO, ETAPAS, SESIONES, TIPOS_ENTRADA, TIPOS_HALLAZGO, TIPOS_INTERACCION, type CuentaDetalle, type Persona, type Prioridad, type Sesion, type TipoEntrada, type TipoHallazgo, type TipoInteraccion, type TipoSesion } from "../api/tipos";
import { Campo, Modal, Seg, grid2 } from "../ui/Modal";
import { hoyISO } from "../ui/primitivas";
import { useAvisos } from "./Aviso";
import { FormArrancar, FormPago, FormPropuesta } from "./FormulariosComercial";

export type Formulario =
  | { tipo: "cuenta" }
  | { tipo: "interaccion"; cuentaId?: string }
  | { tipo: "descubrimiento"; cuentaId: string; empresa: string }
  | { tipo: "sesion"; casoId: string; cuentaId: string; personas: { id: string; nombre: string }[] }
  | { tipo: "persona"; casoId: string; cuentaId: string }
  | { tipo: "hallazgo"; casoId: string; cuentaId: string; personas: Persona[]; sesiones: Sesion[]; sesionId?: string }
  | { tipo: "entrada"; casoId: string; cuentaId: string; sesiones: Sesion[]; sesionId?: string; personas: Persona[] }
  | { tipo: "propuesta"; cuenta: CuentaDetalle; casoId: string | null }
  | { tipo: "pago"; cuenta: CuentaDetalle; proyectoId?: string }
  | { tipo: "arrancar"; cuentaId: string; proyectoId: string; nombre: string; empresa: string };

type Ctx = { abrir: (f: Formulario) => void };
const FormCtx = createContext<Ctx | null>(null);

export function useFormularios(): Ctx {
  const c = useContext(FormCtx);
  if (!c) throw new Error("useFormularios fuera de ProveedorFormularios");
  return c;
}

export function ProveedorFormularios({ children }: { children: ReactNode }) {
  const [f, setF] = useState<Formulario | null>(null);
  const abrir = useCallback((x: Formulario) => setF(x), []);
  const cerrar = useCallback(() => setF(null), []);
  const valor = useMemo(() => ({ abrir }), [abrir]);
  return (
    <FormCtx.Provider value={valor}>
      {children}
      {f?.tipo === "cuenta" && <FormCuenta cerrar={cerrar} />}
      {f?.tipo === "interaccion" && (f.cuentaId ? <FormInteraccion cuentaId={f.cuentaId} cerrar={cerrar} /> : <ElegirCuenta titulo="Registrar interacción" cerrar={cerrar} onElegir={(id) => setF({ tipo: "interaccion", cuentaId: id })} />)}
      {f?.tipo === "descubrimiento" && <FormDescubrimiento cuentaId={f.cuentaId} empresa={f.empresa} cerrar={cerrar} />}
      {f?.tipo === "sesion" && <FormSesion casoId={f.casoId} cuentaId={f.cuentaId} personas={f.personas} cerrar={cerrar} />}
      {f?.tipo === "persona" && <FormPersona casoId={f.casoId} cuentaId={f.cuentaId} cerrar={cerrar} />}
      {f?.tipo === "entrada" && <FormEntrada casoId={f.casoId} cuentaId={f.cuentaId} sesiones={f.sesiones} sesionId={f.sesionId} personas={f.personas} cerrar={cerrar} abrir={abrir} />}
      {f?.tipo === "propuesta" && <FormPropuesta c={f.cuenta} casoId={f.casoId} cerrar={cerrar} />}
      {f?.tipo === "pago" && <FormPago c={f.cuenta} proyectoId={f.proyectoId} cerrar={cerrar} />}
      {f?.tipo === "arrancar" && <FormArrancar cuentaId={f.cuentaId} proyectoId={f.proyectoId} nombre={f.nombre} empresa={f.empresa} cerrar={cerrar} />}
      {f?.tipo === "hallazgo" && <FormHallazgo casoId={f.casoId} cuentaId={f.cuentaId} personas={f.personas} sesiones={f.sesiones} sesionId={f.sesionId} cerrar={cerrar} />}
    </FormCtx.Provider>
  );
}

function mensajeError(e: unknown): string {
  return e instanceof ErrorApi ? e.message : "No se pudo guardar. Revisa la conexión e inténtalo otra vez.";
}

// ─── Nueva cuenta ───────────────────────────────────────────────────────────

function FormCuenta({ cerrar }: { cerrar: () => void }) {
  const nav = useNavigate();
  const { avisar } = useAvisos();
  const crear = useCrearCuenta();
  const [d, setD] = useState({ empresa: "", contacto: "", rol: "", giro: "", ubicacion: "", brief: "", prioridad: "Media" as Prioridad, responsable_id: sesion.quien() || "p-pab", fit: 60, relacional: false, notas: "" });
  const u = <K extends keyof typeof d>(k: K) => (v: (typeof d)[K]) => setD((x) => ({ ...x, [k]: v }));
  const [error, setError] = useState("");
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!d.empresa.trim()) { setError("La empresa necesita nombre."); return; }
    try {
      const c = await crear.mutateAsync(d);
      cerrar();
      avisar(`${c.empresa} creada · sin contactar`);
      nav(`/cuentas/${c.id}`);
    } catch (err) { setError(mensajeError(err)); }
  };
  return (
    <Modal titulo="Nueva cuenta" sub="Lo mínimo para tenerla en el radar. El resto se completa sesión a sesión." onCerrar={cerrar}
      pie={<><button type="button" className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button form="f-cuenta" className="btn btn-primario" disabled={crear.isPending}>Crear cuenta</button></>}>
      <form id="f-cuenta" onSubmit={enviar} style={grid2}>
        <Campo label="Empresa" span error={error}><input autoFocus value={d.empresa} onChange={(e) => u("empresa")(e.target.value)} placeholder="Panadería Ríos" /></Campo>
        <Campo label="Contacto"><input value={d.contacto} onChange={(e) => u("contacto")(e.target.value)} placeholder="Nombre" /></Campo>
        <Campo label="Rol"><input value={d.rol} onChange={(e) => u("rol")(e.target.value)} placeholder="Por confirmar" /></Campo>
        <Campo label="Giro"><input value={d.giro} onChange={(e) => u("giro")(e.target.value)} /></Campo>
        <Campo label="Ubicación"><input value={d.ubicacion} onChange={(e) => u("ubicacion")(e.target.value)} placeholder="Guadalajara, Jalisco" /></Campo>
        <Campo label="Brief" span><textarea value={d.brief} onChange={(e) => u("brief")(e.target.value)} placeholder="Qué hace, qué tan grande es, por qué está en la lista." /></Campo>
        <Campo label="Prioridad"><Seg value={d.prioridad} onChange={u("prioridad")} opciones={["Alta", "Media", "Baja"]} /></Campo>
        <Campo label="Responsable"><Seg value={d.responsable_id} onChange={u("responsable_id")} opciones={EQUIPO.map((p) => [p.id, p.nombre] as [string, string])} /></Campo>
        <Campo label="Fit" pista="0–100, juicio del equipo"><input type="number" min={0} max={100} value={d.fit} onChange={(e) => u("fit")(Number(e.target.value))} /></Campo>
        <Campo label="Origen">
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, padding: "7px 0" }}><input type="checkbox" checked={d.relacional} onChange={(e) => u("relacional")(e.target.checked)} style={{ width: "auto" }} /> Relación personal</label>
        </Campo>
      </form>
    </Modal>
  );
}

// ─── Elegir cuenta (cuando la captura viene sin contexto) ───────────────────

function ElegirCuenta({ titulo, cerrar, onElegir }: { titulo: string; cerrar: () => void; onElegir: (id: string) => void }) {
  const { data: cuentas } = useCuentas();
  const [id, setId] = useState("");
  return (
    <Modal titulo={titulo} sub="¿Con qué cuenta?" onCerrar={cerrar} ancho={440}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className="btn btn-primario" disabled={!id} onClick={() => onElegir(id)}>Continuar</button></>}>
      <Campo label="Cuenta">
        <select autoFocus value={id} onChange={(e) => setId(e.target.value)}>
          <option value="">Elegir cuenta…</option>
          {(cuentas ?? []).map((c) => <option key={c.id} value={c.id}>{c.empresa} · {ETAPAS[c.etapa].label}</option>)}
        </select>
      </Campo>
    </Modal>
  );
}

// ─── Interacción ────────────────────────────────────────────────────────────

// Lo que exporta Plaud (y casi cualquier grabadora). El texto plano se lee aquí
// mismo; PDF y DOCX los convierte la API, que devuelve solo el texto. Lo que se
// guarda con la interacción es siempre texto, nunca el archivo.
const EXT_TEXTO = [".txt", ".md", ".srt", ".vtt"];
const EXT_TRANSCRIPCION = [...EXT_TEXTO, ".pdf", ".docx"];
const MAX_TRANSCRIPCION = 500_000; // caracteres; el mismo tope que en el servidor
const MAX_BYTES = 10 * 1024 * 1024;

type Transcripcion = { nombre: string; texto: string };

function enBase64(f: File): Promise<string> {
  return new Promise((ok, mal) => {
    const r = new FileReader();
    r.onload = () => ok(String(r.result).split(",", 2)[1] ?? "");
    r.onerror = () => mal(new Error("No se pudo leer el archivo."));
    r.readAsDataURL(f);
  });
}

async function leerTranscripcion(f: File): Promise<Transcripcion> {
  const nombre = f.name;
  const ext = nombre.toLowerCase().slice(nombre.lastIndexOf("."));
  if (!nombre.includes(".") || !EXT_TRANSCRIPCION.includes(ext)) {
    throw new Error(`Formato no admitido. Usa ${EXT_TRANSCRIPCION.join(", ")}.`);
  }
  if (f.size > MAX_BYTES) throw new Error("El archivo pasa de 10 MB; no parece una transcripción.");
  let texto: string;
  if (EXT_TEXTO.includes(ext)) {
    texto = (await f.text()).replace(/^﻿/, "").trim();
    if (texto.includes("\u0000")) throw new Error("No parece un archivo de texto.");
  } else {
    texto = (await api<{ texto: string }>("/api/transcripciones/extraer", { body: { nombre, contenido_b64: await enBase64(f) } })).texto;
  }
  if (!texto) throw new Error("El archivo está vacío.");
  if (texto.length > MAX_TRANSCRIPCION) throw new Error(`Pasa de ${MAX_TRANSCRIPCION.toLocaleString("es-MX")} caracteres; recórtalo o divídelo.`);
  return { nombre, texto };
}

/** El botón «Adjuntar archivo…» y, una vez leído, el nombre con su conteo de palabras. */
function AdjuntarTranscripcion({ trans, onCambio, onLeyendo }: { trans: Transcripcion | null; onCambio: (t: Transcripcion | null) => void; onLeyendo: (b: boolean) => void }) {
  const [error, setError] = useState("");
  const [leyendo, setLeyendo] = useState(false);
  const adjuntar = async (f: File | undefined) => {
    if (!f) return;
    setError("");
    setLeyendo(true);
    onLeyendo(true);
    try { onCambio(await leerTranscripcion(f)); } catch (err) { onCambio(null); setError(err instanceof Error ? err.message : mensajeError(err)); } finally { setLeyendo(false); onLeyendo(false); }
  };
  return (
    <Campo label="Transcripción (opcional)" span error={error} pista={trans ? undefined : `De Plaud u otra grabadora: ${EXT_TRANSCRIPCION.join(", ")}. Se guarda solo el texto.`}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {trans ? (
          <>
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13.5 }}>
              {trans.nombre} <span style={{ color: "var(--faint)" }}>· {trans.texto.split(/\s+/).length.toLocaleString("es-MX")} palabras</span>
            </span>
            <button type="button" className="btn btn-secundario" onClick={() => onCambio(null)}>Quitar</button>
          </>
        ) : (
          <label className="btn btn-secundario" style={{ cursor: leyendo ? "progress" : "pointer", opacity: leyendo ? 0.6 : 1 }}>
            {leyendo ? "Leyendo archivo…" : "Adjuntar archivo…"}
            <input type="file" hidden disabled={leyendo} accept={[...EXT_TRANSCRIPCION, "text/plain"].join(",")}
              onChange={(e) => { void adjuntar(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        )}
      </div>
    </Campo>
  );
}

function FormInteraccion({ cuentaId, cerrar }: { cuentaId: string; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const registrar = useRegistrarInteraccion(cuentaId);
  const [d, setD] = useState({ tipo: "reunion" as TipoInteraccion, fecha: hoyISO(), resumen: "", con: "", quien: sesion.quien() || "p-pab", proxima_accion: "", compromiso_fecha: "" });
  const u = <K extends keyof typeof d>(k: K) => (v: (typeof d)[K]) => setD((x) => ({ ...x, [k]: v }));
  const [error, setError] = useState("");
  const [trans, setTrans] = useState<Transcripcion | null>(null);
  const [leyendo, setLeyendo] = useState(false);
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!d.resumen.trim()) { setError("Qué pasó, en una línea."); return; }
    try {
      await registrar.mutateAsync({
        id: idNuevo("i"), ...d, proxima_accion: d.proxima_accion || null, compromiso_fecha: d.compromiso_fecha || null,
        transcripcion: trans?.texto ?? null, transcripcion_archivo: trans?.nombre ?? null,
      });
      cerrar();
      avisar(`${TIPOS_INTERACCION[d.tipo]} registrada`);
    } catch (err) { setError(mensajeError(err)); }
  };
  return (
    <Modal titulo="Registrar interacción" onCerrar={cerrar}
      pie={<><button type="button" className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button form="f-int" className="btn btn-primario" disabled={registrar.isPending || leyendo}>Guardar</button></>}>
      <form id="f-int" onSubmit={enviar} style={grid2}>
        <Campo label="Tipo" span><Seg value={d.tipo} onChange={u("tipo")} opciones={(Object.keys(TIPOS_INTERACCION) as TipoInteraccion[]).map((k) => [k, TIPOS_INTERACCION[k]] as [TipoInteraccion, string])} /></Campo>
        <Campo label="Fecha"><input type="date" value={d.fecha} onChange={(e) => u("fecha")(e.target.value)} /></Campo>
        <Campo label="Por parte de Nexlum"><Seg value={d.quien} onChange={u("quien")} opciones={EQUIPO.map((p) => [p.id, p.nombre] as [string, string])} /></Campo>
        <Campo label="Con quién (del cliente)" span><input value={d.con} onChange={(e) => u("con")(e.target.value)} placeholder="Luis Ramírez, Chuy Ortega" /></Campo>
        <Campo label="Qué pasó" span error={error}><textarea autoFocus value={d.resumen} onChange={(e) => u("resumen")(e.target.value)} placeholder="Una o dos líneas. Lo que se dijo, lo que se acordó." /></Campo>
        <Campo label="Próxima acción"><input value={d.proxima_accion} onChange={(e) => u("proxima_accion")(e.target.value)} placeholder="Agendar El Recorrido" /></Campo>
        <Campo label="Prometí algo para el…" pista="Si hubo compromiso con fecha, aparece en Hoy"><input type="date" value={d.compromiso_fecha} onChange={(e) => u("compromiso_fecha")(e.target.value)} /></Campo>
        <AdjuntarTranscripcion trans={trans} onCambio={setTrans} onLeyendo={setLeyendo} />
      </form>
    </Modal>
  );
}

// ─── Iniciar descubrimiento ─────────────────────────────────────────────────

function FormDescubrimiento({ cuentaId, empresa, cerrar }: { cuentaId: string; empresa: string; cerrar: () => void }) {
  const nav = useNavigate();
  const { avisar } = useAvisos();
  const iniciar = useIniciarDescubrimiento(cuentaId);
  const [d, setD] = useState({ facilitador_id: sesion.quien() || "p-pab", empleados_rango: "50-100", num_sitios: 2, antiguedad_anios: 20, ruta: "larga" as "larga" | "corta", ruta_corta_justificacion: "" });
  const u = <K extends keyof typeof d>(k: K) => (v: (typeof d)[K]) => setD((x) => ({ ...x, [k]: v }));
  const [error, setError] = useState("");
  const nombre = (id: string) => EQUIPO.find((p) => p.id === id)?.nombre ?? id;
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (d.ruta === "corta" && !d.ruta_corta_justificacion.trim()) { setError("La ruta corta exige justificación por escrito (INV-8)."); return; }
    try {
      await iniciar.mutateAsync({ ...d, facilitador_nombre: nombre(d.facilitador_id), ruta_corta_justificacion: d.ruta_corta_justificacion || null });
      cerrar();
      avisar(`${empresa} entra a descubrimiento · D0 Radar`);
      nav(`/cuentas/${cuentaId}/descubrimiento`);
    } catch (err) { setError(mensajeError(err)); }
  };
  return (
    <Modal titulo="Iniciar El Recorrido" sub={`Abre el expediente de descubrimiento de ${empresa}.`} onCerrar={cerrar}
      pie={<><button type="button" className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button form="f-desc" className="btn btn-primario" disabled={iniciar.isPending}>Iniciar descubrimiento</button></>}>
      <form id="f-desc" onSubmit={enviar} style={grid2}>
        <Campo label="Facilitador" span><Seg value={d.facilitador_id} onChange={u("facilitador_id")} opciones={EQUIPO.map((p) => [p.id, p.nombre] as [string, string])} /></Campo>
        <Campo label="Empleados"><select value={d.empleados_rango} onChange={(e) => u("empleados_rango")(e.target.value)}>{["10-50", "50-100", "100-250", "250-500", "500+"].map((r) => <option key={r}>{r}</option>)}</select></Campo>
        <Campo label="Sitios" pista="Con un solo sitio, el método sugiere la ruta corta"><input type="number" min={1} value={d.num_sitios} onChange={(e) => u("num_sitios")(Number(e.target.value))} /></Campo>
        <Campo label="Antigüedad (años)"><input type="number" min={0} value={d.antiguedad_anios} onChange={(e) => u("antiguedad_anios")(Number(e.target.value))} /></Campo>
        <Campo label="Ruta"><Seg value={d.ruta} onChange={u("ruta")} opciones={[["larga", "Larga · 4 sesiones"], ["corta", "Corta"]]} /></Campo>
        {d.ruta === "corta" && <Campo label="Justificación de la ruta corta" span error={error}><textarea value={d.ruta_corta_justificacion} onChange={(e) => u("ruta_corta_justificacion")(e.target.value)} placeholder="Por qué se ahorra una sesión. Las Voces no se ahorran nunca." /></Campo>}
        {d.ruta !== "corta" && error && <p className="error" style={{ gridColumn: "1 / -1", fontSize: 12.5, color: "var(--risk)" }}>{error}</p>}
      </form>
    </Modal>
  );
}

// ─── Sesión ─────────────────────────────────────────────────────────────────

function FormSesion({ casoId, cuentaId, personas, cerrar }: { casoId: string; cuentaId: string; personas: { id: string; nombre: string }[]; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const agregar = useAgregarSesion(casoId, cuentaId);
  const [d, setD] = useState({ tipo: "S1_platica" as TipoSesion, fecha: hoyISO() + "T10:00", asistentes: personas.map((p) => p.id), hubo_grabacion: true, consentimiento_de_todos: true, sin_grabacion_motivo: "" });
  const u = <K extends keyof typeof d>(k: K) => (v: (typeof d)[K]) => setD((x) => ({ ...x, [k]: v }));
  const [error, setError] = useState("");
  const [trans, setTrans] = useState<Transcripcion | null>(null);
  const [leyendo, setLeyendo] = useState(false);
  const toggle = (id: string) => u("asistentes")(d.asistentes.includes(id) ? d.asistentes.filter((x) => x !== id) : [...d.asistentes, id]);
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (d.asistentes.length === 0) { setError("Una sesión sin asistentes no es una sesión."); return; }
    if (d.hubo_grabacion && !d.consentimiento_de_todos) { setError("Toda sesión grabada exige consentimiento de todos (regla absoluta)."); return; }
    if (!d.hubo_grabacion && !d.sin_grabacion_motivo.trim()) { setError("Si no se grabó, hay que decir por qué."); return; }
    try {
      const texto = d.hubo_grabacion ? trans : null;
      await agregar.mutateAsync({ id: idNuevo("s"), ...d, sin_grabacion_motivo: d.sin_grabacion_motivo || null, transcripcion: texto?.texto ?? null, transcripcion_archivo: texto?.nombre ?? null });
      cerrar();
      avisar(`${SESIONES.find((s) => s.id === d.tipo)?.nombre} registrada`);
    } catch (err) { setError(mensajeError(err)); }
  };
  return (
    <Modal titulo="Registrar sesión" sub="Se registra después de tenerla. Los hallazgos se vuelcan luego, en Verificación." onCerrar={cerrar}
      pie={<><button type="button" className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button form="f-ses" className="btn btn-primario" disabled={agregar.isPending || leyendo}>Guardar sesión</button></>}>
      <form id="f-ses" onSubmit={enviar} style={grid2}>
        <Campo label="Sesión" span><Seg value={d.tipo} onChange={u("tipo")} opciones={SESIONES.map((s) => [s.id, `${s.num} · ${s.nombre}`] as [TipoSesion, string])} /></Campo>
        <Campo label="Fecha y hora"><input type="datetime-local" value={d.fecha} onChange={(e) => u("fecha")(e.target.value)} /></Campo>
        <Campo label="Grabación"><Seg value={d.hubo_grabacion ? "si" : "no"} onChange={(v) => u("hubo_grabacion")(v === "si")} opciones={[["si", "Con Plaud"], ["no", "Sin grabar"]]} /></Campo>
        <Campo label="Asistentes" span error={error}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {personas.map((p) => <button key={p.id} type="button" className={"chip" + (d.asistentes.includes(p.id) ? " activo" : "")} onClick={() => toggle(p.id)}>{p.nombre}</button>)}
          </div>
        </Campo>
        {d.hubo_grabacion ? (
          <>
            <Campo label="Consentimiento" span><label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, padding: "7px 0" }}><input type="checkbox" checked={d.consentimiento_de_todos} onChange={(e) => u("consentimiento_de_todos")(e.target.checked)} style={{ width: "auto" }} /> Todos dieron consentimiento verbal</label></Campo>
            <AdjuntarTranscripcion trans={trans} onCambio={setTrans} onLeyendo={setLeyendo} />
          </>
        ) : (
          <Campo label="Por qué no se grabó" span><input value={d.sin_grabacion_motivo} onChange={(e) => u("sin_grabacion_motivo")(e.target.value)} /></Campo>
        )}
      </form>
    </Modal>
  );
}

// ─── Persona ────────────────────────────────────────────────────────────────

function FormPersona({ casoId, cuentaId, cerrar }: { casoId: string; cuentaId: string; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const agregar = useAgregarPersona(casoId, cuentaId);
  const [d, setD] = useState({ nombre: "", rol: "", es_economic_buyer: false, puede_vetar: false, es_champion: false, es_frontline: false });
  const u = <K extends keyof typeof d>(k: K) => (v: (typeof d)[K]) => setD((x) => ({ ...x, [k]: v }));
  const [error, setError] = useState("");
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!d.nombre.trim()) { setError("Falta el nombre."); return; }
    try {
      await agregar.mutateAsync({ id: idNuevo("p"), organizacion: "cliente", ...d });
      cerrar();
      avisar(`${d.nombre} agregado al expediente`);
    } catch (err) { setError(mensajeError(err)); }
  };
  const flags: [keyof typeof d, string][] = [["es_economic_buyer", "Firma (economic buyer)"], ["puede_vetar", "Puede vetar"], ["es_champion", "Champion"], ["es_frontline", "Frontline"]];
  return (
    <Modal titulo="Persona del cliente" sub="Quién es y qué puede. La gobernanza queda como afirmación hasta que un hallazgo la respalde." onCerrar={cerrar}
      pie={<><button type="button" className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button form="f-per" className="btn btn-primario" disabled={agregar.isPending}>Agregar</button></>}>
      <form id="f-per" onSubmit={enviar} style={grid2}>
        <Campo label="Nombre" error={error}><input autoFocus value={d.nombre} onChange={(e) => u("nombre")(e.target.value)} /></Campo>
        <Campo label="Rol"><input value={d.rol} onChange={(e) => u("rol")(e.target.value)} placeholder="Jefe de producción" /></Campo>
        <div style={{ gridColumn: "1 / -1", display: "flex", flexWrap: "wrap", gap: 6 }}>
          {flags.map(([k, l]) => <button key={k} type="button" className={"chip" + (d[k] ? " activo" : "")} onClick={() => u(k)(!d[k] as never)}>{l}</button>)}
        </div>
      </form>
    </Modal>
  );
}

// ─── Hallazgo ───────────────────────────────────────────────────────────────
//
// Captura posterior: con la libreta y la grabación al lado. Nace como candidato y
// se verifica después en la cola. El fragmento textual es lo que se contrasta al
// verificar, así que se pide aunque no sea obligatorio. El formulario se queda
// abierto tras guardar: en una sesión salen quince hallazgos seguidos.

type Tema = "desplazamiento" | "control" | "calidad" | "carga" | "otro";
type Intensidad = 1 | 2 | 3 | 4 | 5;
type Frecuencia = "diaria" | "semanal" | "mensual" | "esporadica";

function FormHallazgo({ casoId, cuentaId, personas, sesiones, sesionId, cerrar }: { casoId: string; cuentaId: string; personas: Persona[]; sesiones: Sesion[]; sesionId?: string; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const proponer = useProponerHallazgo(casoId, cuentaId);
  const { data: zonas } = useZonas(casoId);
  const activas = (zonas ?? []).filter((z) => z.estado !== "descartada");
  const cliente = personas.filter((p) => p.organizacion === "cliente");
  const nombreSesion = (s: Sesion) => `${SESIONES.find((x) => x.id === s.tipo)?.nombre ?? s.tipo} · ${s.fecha.slice(0, 10)}`;
  const [d, setD] = useState({
    tipo: "dolor" as TipoHallazgo, contenido: "", persona_id: cliente[0]?.id ?? "", sesion_id: sesionId ?? sesiones[sesiones.length - 1]?.id ?? "", min: "", seg: "", fragmento: "", zona: "",
    valor: "", unidad: "", periodo: "", fuente_dato: "", es_candidato_baseline: false, intensidad: 3 as Intensidad, tema: "otro" as Tema, mitigable: true, frecuencia: "esporadica" as Frecuencia,
  });
  const u = <K extends keyof typeof d>(k: K) => (v: (typeof d)[K]) => setD((x) => ({ ...x, [k]: v }));
  const [error, setError] = useState("");
  const [guardados, setGuardados] = useState(0);
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!d.contenido.trim()) { setError("El hallazgo, en una línea: qué se dijo o se vio."); return; }
    if (!d.persona_id) { setError("¿De quién es? Agrega primero a la persona del cliente."); return; }
    if (!d.sesion_id) { setError("¿En qué sesión? Registra primero la sesión."); return; }
    if (d.tipo === "metrica" && (!d.valor || !d.unidad || !d.fuente_dato)) { setError("Una métrica necesita valor, unidad y de dónde sale el dato."); return; }
    const ms = (Number(d.min) || 0) * 60000 + (Number(d.seg) || 0) * 1000;
    const atributos = d.tipo === "metrica" ? { valor: Number(d.valor), unidad: d.unidad, periodo: d.periodo || "—", fuente_dato: d.fuente_dato, es_candidato_baseline: d.es_candidato_baseline }
      : d.tipo === "ansiedad" ? { intensidad: d.intensidad, tema: d.tema, mitigable: d.mitigable }
      : d.tipo === "dolor" ? { frecuencia: d.frecuencia } : null;
    try {
      await proponer.mutateAsync({ id: idNuevo("h"), tipo: d.tipo, contenido: d.contenido.trim(), persona_id: d.persona_id, sesion_id: d.sesion_id, timestamp_ms: ms, fragmento_transcripcion: d.fragmento.trim() || null, atributos, zona_caliente_id: d.zona || null });
      setGuardados((n) => n + 1);
      setError("");
      setD((x) => ({ ...x, contenido: "", fragmento: "", valor: "", min: "", seg: "" }));
      avisar(`Hallazgo propuesto · ${TIPOS_HALLAZGO[d.tipo]}. Sigue en la cola de verificación.`);
    } catch (err) { setError(mensajeError(err)); }
  };
  return (
    <Modal titulo="Proponer hallazgo" sub={`Nace como candidato; entra al grafo cuando alguien lo verifica.${guardados ? ` ${guardados} guardado${guardados === 1 ? "" : "s"} en esta tanda.` : ""}`} onCerrar={cerrar} ancho={600}
      pie={<><button type="button" className="btn btn-secundario" onClick={cerrar}>{guardados ? "Terminar" : "Cancelar"}</button><button form="f-hal" className="btn btn-primario" disabled={proponer.isPending}>Guardar y otro</button></>}>
      <form id="f-hal" onSubmit={enviar} style={grid2}>
        <Campo label="Tipo" span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(Object.keys(TIPOS_HALLAZGO) as TipoHallazgo[]).map((t) => <button key={t} type="button" className={"chip" + (d.tipo === t ? " activo" : "")} onClick={() => u("tipo")(t)}>{TIPOS_HALLAZGO[t]}</button>)}
          </div>
        </Campo>
        <Campo label="Hallazgo" span error={error}><textarea autoFocus value={d.contenido} onChange={(e) => u("contenido")(e.target.value)} placeholder="El reporte de producción se arma a mano cada mañana; toma ~2 horas." /></Campo>
        <Campo label="Quién lo dijo"><select value={d.persona_id} onChange={(e) => u("persona_id")(e.target.value)}>{cliente.length === 0 && <option value="">— sin personas —</option>}{cliente.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></Campo>
        <Campo label="Sesión"><select value={d.sesion_id} onChange={(e) => u("sesion_id")(e.target.value)}>{sesiones.length === 0 && <option value="">— sin sesiones —</option>}{sesiones.map((s) => <option key={s.id} value={s.id}>{nombreSesion(s)}</option>)}</select></Campo>
        <Campo label="Minuto en la grabación" pista="mm : ss">
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}><input type="number" min={0} value={d.min} onChange={(e) => u("min")(e.target.value)} placeholder="22" style={{ width: 70 }} /><span>:</span><input type="number" min={0} max={59} value={d.seg} onChange={(e) => u("seg")(e.target.value)} placeholder="14" style={{ width: 70 }} /></div>
        </Campo>
        <Campo label="Lo que se dijo, textual" span pista="Es lo que se contrasta al verificar."><textarea value={d.fragmento} onChange={(e) => u("fragmento")(e.target.value)} placeholder="…pues el reporte lo arma Chuy a mano cada mañana, se lleva como dos horas…" /></Campo>
        {activas.length > 0 && (
          // Asignar la zona es lo que le da campo a esa zona: las compuertas de D2
          // piden una observación o métrica verificada por cada zona finalista.
          <Campo label="Zona caliente" span pista="Opcional, pero es lo que acredita a la zona para pasar de fase">
            <select value={d.zona} onChange={(e) => u("zona")(e.target.value)}>
              <option value="">— sin asignar —</option>
              {activas.map((z) => <option key={z.id} value={z.id}>{z.nombre}</option>)}
            </select>
          </Campo>
        )}
        {d.tipo === "metrica" && <>
          <Campo label="Valor"><input type="number" step="any" value={d.valor} onChange={(e) => u("valor")(e.target.value)} /></Campo>
          <Campo label="Unidad"><input value={d.unidad} onChange={(e) => u("unidad")(e.target.value)} placeholder="% por turno" /></Campo>
          <Campo label="Periodo"><input value={d.periodo} onChange={(e) => u("periodo")(e.target.value)} placeholder="semanal" /></Campo>
          <Campo label="De dónde sale el dato"><input value={d.fuente_dato} onChange={(e) => u("fuente_dato")(e.target.value)} placeholder="Excel de Chuy · hoja Merma" /></Campo>
          <Campo label="Baseline" span><label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, padding: "4px 0" }}><input type="checkbox" checked={d.es_candidato_baseline} onChange={(e) => u("es_candidato_baseline")(e.target.checked)} style={{ width: "auto" }} /> Candidata a baseline (la compuerta 2 la exige)</label></Campo>
        </>}
        {d.tipo === "ansiedad" && <>
          <Campo label="Intensidad"><Seg value={String(d.intensidad)} onChange={(v) => u("intensidad")(Number(v) as Intensidad)} opciones={["1", "2", "3", "4", "5"]} /></Campo>
          <Campo label="Tema"><select value={d.tema} onChange={(e) => u("tema")(e.target.value as Tema)}>{["desplazamiento", "control", "calidad", "carga", "otro"].map((t) => <option key={t}>{t}</option>)}</select></Campo>
          <Campo label="Mitigable" span><Seg value={d.mitigable ? "si" : "no"} onChange={(v) => u("mitigable")(v === "si")} opciones={[["si", "Sí, con diseño"], ["no", "No"]]} /></Campo>
        </>}
        {d.tipo === "dolor" && <Campo label="Frecuencia" span><Seg value={d.frecuencia} onChange={u("frecuencia")} opciones={["diaria", "semanal", "mensual", "esporadica"]} /></Campo>}
      </form>
    </Modal>
  );
}

// ─── Entrada (evidencia de una sesión) ──────────────────────────────────────
//
// Qué se trajo de la sesión: grabación, resumen Plaud, fotos, video, nota,
// documento. Por ahora se registra el nombre del archivo, no el binario: la
// subida real llega con la transcripción automática (Fase 1).

function FormEntrada({ casoId, cuentaId, sesiones, sesionId, personas, cerrar, abrir }: { casoId: string; cuentaId: string; sesiones: Sesion[]; sesionId?: string; personas: Persona[]; cerrar: () => void; abrir: (f: Formulario) => void }) {
  const { avisar } = useAvisos();
  const agregar = useAgregarEntrada(casoId, cuentaId);
  const nombreSesion = (s: Sesion) => `${SESIONES.find((x) => x.id === s.tipo)?.nombre ?? s.tipo} · ${s.fecha.slice(0, 10)}`;
  const [d, setD] = useState({ sesionId: sesionId ?? sesiones[sesiones.length - 1]?.id ?? "", tipo: "audio" as TipoEntrada, etiqueta: "", detalle: "", archivo: "" });
  const u = <K extends keyof typeof d>(k: K) => (v: (typeof d)[K]) => setD((x) => ({ ...x, [k]: v }));
  const [error, setError] = useState("");
  const onArchivo = (f: File | undefined) => {
    if (!f) return;
    const tipo: TipoEntrada = f.type.startsWith("audio") ? "audio" : f.type.startsWith("image") ? "foto" : f.type.startsWith("video") ? "video" : "doc";
    setD((x) => ({ ...x, archivo: f.name, tipo, etiqueta: x.etiqueta || f.name.replace(/\.[^.]+$/, ""), detalle: x.detalle || `${(f.size / 1048576).toFixed(1)} MB` }));
  };
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!d.sesionId) { setError("Primero registra la sesión de la que viene."); return; }
    if (d.etiqueta.trim().length < 2) { setError("Ponle nombre: «Plaud · La Plática», «Pizarrón de Luis».") ; return; }
    try {
      await agregar.mutateAsync({ sesionId: d.sesionId, id: idNuevo("e"), tipo: d.tipo, etiqueta: d.etiqueta.trim(), detalle: d.detalle.trim(), referencia: d.archivo || null, agregada_por: sesion.quien() || null });
      cerrar();
      const s = sesiones.find((x) => x.id === d.sesionId);
      avisar(`${TIPOS_ENTRADA[d.tipo].label} agregada a ${s ? SESIONES.find((x) => x.id === s.tipo)?.nombre : "la sesión"}`);
    } catch (err) { setError(mensajeError(err)); }
  };
  const ph = d.tipo === "audio" ? "Plaud · La Plática" : d.tipo === "foto" ? "Pizarrón de la sala" : d.tipo === "nota" ? "Libreta de Pablo" : "Descripción corta";
  return (
    <Modal titulo="Agregar evidencia" sub="Las entradas alimentan la transcripción y los hallazgos propuestos." onCerrar={cerrar}
      pie={<><button type="button" className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button form="f-ent" className="btn btn-primario" disabled={agregar.isPending || sesiones.length === 0}>Agregar</button></>}>
      {sesiones.length === 0 ? (
        <div className="abierto" style={{ padding: "12px 14px", fontSize: 13.5, lineHeight: 1.5 }}>
          Todavía no hay sesiones en este expediente. La evidencia cuelga de una sesión (fecha, asistentes, consentimiento), así que primero hay que registrarla.
          <div style={{ marginTop: 10 }}><button type="button" className="btn btn-primario" onClick={() => abrir({ tipo: "sesion", casoId, cuentaId, personas: personas.map((p) => ({ id: p.id, nombre: p.nombre })) })}>Registrar sesión →</button></div>
        </div>
      ) : (
        <form id="f-ent" onSubmit={enviar} style={grid2}>
          <Campo label="Sesión" span><select value={d.sesionId} onChange={(e) => u("sesionId")(e.target.value)}>{sesiones.map((s) => <option key={s.id} value={s.id}>{nombreSesion(s)}</option>)}</select></Campo>
          <Campo label="Tipo" span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {(Object.keys(TIPOS_ENTRADA) as TipoEntrada[]).map((t) => <button key={t} type="button" className={"chip" + (d.tipo === t ? " activo" : "")} onClick={() => u("tipo")(t)}>{TIPOS_ENTRADA[t].label}</button>)}
            </div>
          </Campo>
          <Campo label="Archivo" pista="Se registra el nombre; la subida del archivo llega con la transcripción automática." span>
            <label className="abierto" style={{ padding: "8px 10px", cursor: "pointer", display: "flex", gap: 8, alignItems: "center", fontSize: 13, borderRadius: "var(--r-control)" }}>
              <input type="file" onChange={(e) => onArchivo(e.target.files?.[0])} style={{ display: "none" }} />
              <span style={{ fontWeight: 500 }}>{d.archivo || "Elegir archivo…"}</span><span style={{ marginLeft: "auto", fontSize: 12, opacity: .8 }}>grabación · foto · video · PDF</span>
            </label>
          </Campo>
          <Campo label="Nombre" span error={error}><input autoFocus value={d.etiqueta} onChange={(e) => u("etiqueta")(e.target.value)} placeholder={ph} /></Campo>
          <Campo label="Detalle" pista="duración, páginas, cantidad" span><input value={d.detalle} onChange={(e) => u("detalle")(e.target.value)} placeholder="52:40 · 3 páginas · 11 fotos" /></Campo>
        </form>
      )}
    </Modal>
  );
}
